import { computed, ref, unref, watchEffect } from 'vue';

import { getPropertyFromItem, propsFactory, wrapInArray } from '@components/utils';

import type { IInternalItem } from './items';
import type { MaybeRef } from '@components/utils';
import type { PropType, Ref } from 'vue';

export type TFilterMath = boolean | number | [number, number] | [number, number][];

export interface IFilterProps {
  customFilter?: TFilterFunction;
  customKeyFilter?: TFilterKeyFunctions;
  filterKeys?: TFilterKeys;
  filterMode?: TFilterMode;
  noFilter?: boolean;
}

export interface IUseFilter<T> {
  getMatches: (item: T) => Record<string, TFilterMath> | undefined;
  filteredItems: Ref<T[]>;
  filteredMatches: Ref<Map<unknown, Record<string, TFilterMath>>>;
}

export const makeFilterProps = propsFactory(
  {
    /**
     * Функция, используемая для фильтрации элементов списка.
     * Вызывается для каждого фильтруемого ключа и для каждого элемента списка.
     * Первый аргумент - это фильтруемое значение, второй - поисковой запрос, а третий - внутренний объект элемента списка.
     * Функция должна возвращать true если элемент должен быть включен в результаты фильтрации, или индекс соответствия в значении, если он должен быть включен с выделенным результатом.
     */
    customFilter: {
      type: Function as PropType<TFilterFunction>,
      default: null
    },
    /**
     * Функция, используемая для определенных ключей внутри объекта элемента списка. customFilter не будет учитывать элементы с указанным customKeyFilter
     */
    customKeyFilter: {
      type: Object as PropType<TFilterKeyFunctions>,
      default: null
    },
    /**
     * Массив ключей, по которым будет производиться фильтрация
     */
    filterKeys: {
      type: [Array, String] as PropType<TFilterKeys>,
      default: ''
    },
    /**
     * Управляет объединением результатов customFilter и customKeyFilter. Все варианты применяют customFilter только к элементам, не указанным в customKeyFilter.
     * - 'some' - имеется хотя бы одно совпадение либо с customFilter, либо с customKeyFilter
     * - 'every' - все элементы соответствуют либо customFilter, либо customKeyFilter
     * - 'union' - имеется хотя бы одно совпадение из customFilter и все совпадения из customKeyFilter
     * - 'intersection' - имеется хотя бы одно совпадение из customFilter и все совпадения из customKeyFilter
     */
    filterMode: {
      type: String as PropType<TFilterMode>,
      default: 'intersection'
    },
    /**
     * Не применяйте фильтрацию при поиске. Полезно, когда данные фильтруются на стороне сервера
     */
    noFilter: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  },
  'filter'
);

export function useFilter<T extends IInternalItem>(
  props: IFilterProps,
  items: MaybeRef<T[]>,
  query: Ref<string | number | undefined> | (() => string | undefined),
  options?: {
    transform?: (item: T) => {};
  }
): IUseFilter<T> {
  const filteredItems: Ref<T[]> = ref([]);
  const filteredMatches: Ref<Map<unknown, Record<string, TFilterMath>>> = ref(new Map());
  const transformedItems = computed(() =>
    options?.transform ? unref(items).map((item) => [item, options.transform!(item)] as const) : unref(items)
  );

  watchEffect(() => {
    const _query = typeof query === 'function' ? query() : unref(query);
    const strQuery = typeof _query !== 'string' && typeof _query !== 'number' ? '' : String(_query);

    const results = filterItems(unref(transformedItems), strQuery, {
      customKeyFilter: props.customKeyFilter,
      default: props.customFilter,
      filterKeys: props.filterKeys,
      filterMode: props.filterMode,
      noFilter: props.noFilter
    });

    const originalItems = unref(items);

    const _filteredItems: typeof filteredItems['value'] = [];
    const _filteredMatches: typeof filteredMatches['value'] = new Map();

    results.forEach(({ index, matches }) => {
      const item = originalItems[index];
      _filteredItems.push(item);
      _filteredMatches.set(item.value, matches);
    });

    filteredItems.value = _filteredItems;
    filteredMatches.value = _filteredMatches;
  });

  function getMatches(item: T): Record<string, TFilterMath> | undefined {
    return filteredMatches.value.get(item.value);
  }

  return { filteredItems, filteredMatches, getMatches };
}

export function defaultFilter(value: string, query: string): TFilterMath {
  if (value == null || query == null) return -1;
  return value.toString().toLocaleLowerCase().indexOf(query.toString().toLocaleLowerCase());
}

export function filterItems(
  items: readonly (readonly [item: IInternalItem, transformed: {}])[] | readonly IInternalItem[],
  query: string,
  options: Omit<IFilterProps, 'customFilter'> & { default?: TFilterFunction }
): TFilteredItems[] {
  const array: TFilteredItems[] = [];
  const filter = options?.default ?? defaultFilter;
  const keys = options?.filterKeys ? wrapInArray(options.filterKeys) : false;
  const customFilterLength = Object.keys(options?.customKeyFilter ?? {}).length;

  if (!items?.length) return array;

  loop: for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const customMatches: Record<string, TFilterMath> = {};
    const defaultMatches: Record<string, TFilterMath> = {};
    let match: TFilterMath = -1;

    if (query && !options?.noFilter) {
      if (typeof item === 'object') {
        const filterKeys = keys || Object.keys(item);

        for (const key of filterKeys) {
          const value = getPropertyFromItem(item, key, item);
          const keyFilter = options?.customKeyFilter?.[key];

          match = keyFilter ? keyFilter(value, query, item) : filter(value, query, item);

          if (match !== -1 && match !== false) {
            if (keyFilter) {
              customMatches[key] = match;
            } else {
              defaultMatches[key] = match;
            }
          } else if (options?.filterMode === 'every') {
            continue loop;
          }
        }
      } else {
        match = filter(item, query, item);

        if (match !== -1 && match !== false) {
          defaultMatches.title = match;
        }
      }

      const defaultMatchesLength = Object.keys(defaultMatches).length;
      const customMatchesLength = Object.keys(customMatches).length;

      if (!defaultMatchesLength && !customMatchesLength) continue;

      if (options?.filterMode === 'union' && customMatchesLength !== customFilterLength && !defaultMatchesLength)
        continue;

      if (
        options?.filterMode === 'intersection' &&
        (customMatchesLength !== customFilterLength || !defaultMatchesLength)
      )
        continue;
    }

    array.push({ index: i, matches: { ...defaultMatches, ...customMatches } });
  }

  return array;
}

type TFilteredItems = { index: number; matches: Record<string, TFilterMath> };
type TFilterFunction = (value: string, query: string, item?: any) => TFilterMath;
type TFilterKeyFunctions = Record<string, TFilterFunction>;
type TFilterKeys = string | string[];
type TFilterMode = 'some' | 'every' | 'union' | 'intersection';
